import React, { useState, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) return;

    // 🧹 Limpiar cualquier sesión anterior para evitar conflictos
    localStorage.clear();
    sessionStorage.clear();

    if (sessionStorage.getItem('sso_processed') === token) return;
    sessionStorage.setItem('sso_processed', token);

    const attemptValidation = (attempt) => {
      console.log(`Intento SSO ${attempt} - URL base:`, process.env.REACT_APP_URL_BASE_BACKEND);
      fetch(`${process.env.REACT_APP_URL_BASE_BACKEND}/auth/validate-sso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssoToken: token }),
        cache: 'no-store',
      })
        .then(res => {
          if (!res.ok) throw new Error('Token SSO no válido');
          return res.json();
        })
        .then(data => {
          // 🧹 Limpiar cualquier sesión anterior para evitar conflictos
          localStorage.clear();
          sessionStorage.clear();
          // 💾 Guardar la nueva sesión
          localStorage.setItem('token', data.token);
          localStorage.setItem('userData', JSON.stringify(data.user));
          login(data.token, data.user);
          navigate('/');
        })
        .catch(err => {
          console.warn(`SSO intento ${attempt} falló:`, err);
          if (attempt < 3) {
            // ⏳ Esperar un poco y reintentar
            setTimeout(() => attemptValidation(attempt + 1), 1000 * attempt);
          } else {
            // Agotados los reintentos, mostrar login tradicional
            sessionStorage.removeItem('sso_processed');
          }
        });
    };

    attemptValidation(1);
  }, []);

  const handleCaptchaChange = token => {
    console.log('Captcha token recibido:', token);
    setCaptchaToken(token);
  };

  const handleSubmit = async e => {
    e.preventDefault();
  
    if (!email || !password) {
      setError('Por favor, ingresa tu correo electrónico y contraseña.');
      return;
    }
  
    // if (!captchaToken) {
    //   setError('Por favor, completa el reCAPTCHA');
    //   return;
    // }
  
    try {
      const response = await fetch(
        process.env.REACT_APP_URL_BASE_BACKEND + `/login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name, email, password, captchaToken }),
        }
      );
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Credenciales incorrectas');
      }
  
      const data = await response.json();
      
      // Guardar datos completos del usuario en localStorage
      localStorage.setItem('userData', JSON.stringify(data.user));
      
      // Ejecutar la función login con los datos del usuario
      login(data.token, data.user);
      navigate('/'); // Redirigir al usuario a la página principal
    } catch (error) {
      setError(error.message); // Mostrar el mensaje de error específico
      console.error('Error:', error);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="flex bg-white rounded-lg shadow-lg w-full max-w-4xl overflow-hidden">
        <div className="grid grid-cols-1 w-full">
          <h2 className="text-3xl font-bold mb-6 text-center flex justify-center m-5 ">
            Tu propuesta
          </h2>
          {/* Contenedor de la imagen */}
          <div className=" w-1/2 flex items-center justify-center p-8 ml-28 mb-32">
            <img
              src={process.env.REACT_APP_LOGO_URL || '/Logo%20Yes%20BPO_Mesa%20de%20trabajo%201.png'}
              alt="Logo"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
        {/* Formulario de inicio de sesión */}
        <div className="w-full p-8 flex flex-col">
          <h2 className="text-2xl font-bold mb-6 text-center">
            Iniciar Sesión
          </h2>
          {error && <div className="text-error mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="flex flex-col flex-grow">
            <div className="form-control mb-4">
              <label className="label">
                <span className="label-text">Correo electrónico</span>
              </label>
              <input
                type="email"
                placeholder="Correo electrónico"
                className="input input-bordered w-full rounded-md py-2 px-3 border"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-control mb-6 relative">
              <label className="label">
                <span className="label-text">Contraseña</span>
              </label>
              <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Contraseña"
                className="input input-bordered w-full rounded-md py-2 px-3 border"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 px-3 flex items-center" onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            </div>
            <div className="form-control mb-6 flex justify-center items-center">
              <ReCAPTCHA
                sitekey="6LfGY8EqAAAAAMGDD42nA8i1Q0nLDyq2Etwcv41L"
                onChange={handleCaptchaChange}
              />
            </div>
            <div className="mt-auto">
              <button
                type="submit"
                className="btn w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-md"
              >
                Iniciar Sesión
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;

